/**
 * MainLayout Component
 * Main application layout with header, sidebar, and content area.
 *
 * The sidebar can be collapsed on desktop (icons only, no labels) via an
 * inline toggle inside the drawer. The preference is persisted in
 * localStorage so the user keeps it across reloads.
 *
 * The sidebar can also be **fully hidden** (not just collapsed) via the
 * `SidebarVisibilityContext`. This is the third state on top of expanded
 * (240px) and collapsed (64px): hidden = sidebar not rendered at all and
 * the main content takes 100% of the viewport width. The toggle is wired
 * from the WebRTCPage's `FloatingControlBar` (the main use case is to
 * maximize the remote view). A small floating "show sidebar" button lives
 * on the left edge of the viewport when the sidebar is hidden so the
 * user can always restore it from any page.
 *
 * The notifications provider wraps the whole layout so the bell + panel
 * can react to live updates (enrollments, chats, agents, update-states).
 *
 * On mobile, a horizontal-right swipe starting inside the left edge of the
 * viewport opens the side menu instead of triggering the browser's native
 * back-navigation gesture (Chrome on Android, Safari on iOS). The gesture
 * is wired via `useEdgeSwipe`, which captures the touchmove and calls
 * `preventDefault()` to override the browser's back-nav. On desktop the
 * hook is inert — the sidebar is permanent there and a left-edge swipe
 * would feel intrusive.
 */

import { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Toolbar, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAgentsStore } from '@/store/agentsStore';
import { useEdgeSwipe } from '@/hooks/useEdgeSwipe';
import Header from './Header';
import Sidebar, { DRAWER_COLLAPSED_WIDTH } from './Sidebar';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { NotificationsProvider } from '@/providers/NotificationsProvider';
import { NotificationsLocationSync } from '@/components/notifications/NotificationsLocationSync';
import {
  SidebarVisibilityProvider,
  useSidebarVisibility,
} from '@/contexts/SidebarVisibilityContext';

interface MainLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'sidebar.collapsed';
const SIDEBAR_EXPANDED_WIDTH = 240;

function readInitialCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function MainLayout({ children }: MainLayoutProps) {
  return (
    <NotificationsProvider>
      {/*
        SidebarVisibilityProvider is placed at the layout root so any
        descendant (Sidebar, FloatingControlBar, page components) can
        read/toggle the hidden state. The NotificationsProvider is
        deliberately outside it because it doesn't need the visibility
        context.
      */}
      <SidebarVisibilityProvider>
        <MainLayoutContent>{children}</MainLayoutContent>
      </SidebarVisibilityProvider>
    </NotificationsProvider>
  );
}

function MainLayoutContent({ children }: MainLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => readInitialCollapsed());
  const { hidden, setHidden } = useSidebarVisibility();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { setFilters } = useAgentsStore();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Persist collapsed preference
  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // Ignore quota / privacy errors silently
    }
  }, [collapsed]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleToggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const handleShowSidebar = useCallback(() => {
    setHidden(false);
  }, [setHidden]);

  // Mobile-only: a horizontal-right swipe starting in the left ~24px of
  // the viewport opens the menu drawer instead of triggering the browser's
  // native back-nav gesture. `setMobileOpen(true)` is idempotent — if the
  // menu is already open, the call is a no-op. The hook itself does not
  // close the drawer; users dismiss by tapping outside or the hamburger
  // button.
  useEdgeSwipe({
    enabled: isMobile,
    onSwipe: () => setMobileOpen(true),
  });

  const handleSearch = (query: string) => {
    // Update agents filters and navigate to agents list when searching
    const trimmed = query?.trim() ?? '';
    setFilters({ search: trimmed });

    // If there is a query, ensure user is on the agents page
    if (trimmed && location.pathname !== '/agents') {
      navigate('/agents');
    }
  };

  // Compute the offset the main content needs to subtract for the sidebar.
  // When `hidden`, the sidebar is not rendered, so main fills 100%.
  // The mobile drawer is a temporary overlay (not part of layout flow) and
  // is therefore always treated as 0px in width calculations.
  const sidebarWidth = hidden ? 0 : collapsed ? DRAWER_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  return (
    <>
      {/* Outer shell locked to the viewport — `100dvh` on mobile adjusts when
          the browser chrome (URL bar) appears/disappears, avoiding the
          rubber-band body scroll. `overflow: hidden` is a hard guarantee that
          the body never scrolls; pages that need their own internal scroll
          (e.g. AgentsPage, TerminalPage) declare it themselves. */}
      <Box sx={{ display: 'flex', height: { xs: '100dvh', sm: '100vh' }, overflow: 'hidden' }}>
        {/* PWA install/update prompts — solo en el panel admin autenticado,
            nunca en la página pública (DownloadPage). */}
        <PWAInstallPrompt />
        {/* Sincroniza el notificationsStore con cambios de ruta. */}
        <NotificationsLocationSync />

        {/* Header */}
        <Header onMenuClick={handleDrawerToggle} onSearch={handleSearch} />

        {/* Sidebar (with inline collapse toggle on desktop). Hidden when
            `hidden` is true (see SidebarVisibilityContext) — we just skip
            rendering to keep the DOM clean and the main content fully
            expanded. The mobile temporary drawer inside Sidebar still
            gets rendered (it is a separate <Drawer variant="temporary">)
            but it only opens on hamburger/swipe gestures and never takes
            up layout flow, so the `hidden` flag only controls the
            permanent desktop drawer in practice. */}
        {!hidden && (
          <Sidebar
            open={mobileOpen}
            onClose={handleDrawerToggle}
            variant="permanent"
            collapsed={collapsed}
            onToggleCollapsed={handleToggleCollapsed}
          />
        )}

        {/* Main content area — flex column so child pages can use `flex: 1`
            to fill the remaining viewport (see AgentsPage). `minHeight: 0` is
            required so flex children can shrink below their intrinsic size
            and trigger inner `overflow: auto`. */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            // `minWidth: 0` lets this flex item shrink below its children's
            // intrinsic content width — without it, a wide toolbar/card
            // inside pushes <main> wider than the viewport and the body
            // starts scrolling horizontally. `overflowX: 'hidden'` is the
            // hard guarantee: even if some descendant forgets the
            // `minWidth: 0` rule, content cannot leak past the page edge.
            minWidth: 0,
            overflowX: 'hidden',
            minHeight: 0,
            p: 3,
            // The <Toolbar /> spacer below already matches the AppBar height,
            // so any extra top padding on <main> shows up as empty space
            // between the header and the content. Use 0 on mobile, keep the
            // normal padding on tablet/desktop.
            pt: { xs: 0, sm: 3 },
            width: {
              sm: `calc(100% - ${sidebarWidth}px)`,
            },
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
          }}
        >
          <Toolbar /> {/* Spacer for AppBar */}
          {/* No extra top margin: the search input now lives inside the
              AppBar (single row), so the Toolbar spacer above is enough. */}
          {children}
        </Box>
      </Box>

      {/* Floating "show sidebar" handle. Only visible on desktop (sm+)
          and only when the sidebar is fully hidden. It is the user's
          escape hatch to bring the sidebar back if they hid it on a
          different page (e.g. hid it from the WebRTCPage and then
          navigated to /agents). Position: fixed so it always sits on the
          left edge of the viewport, vertically centered. zIndex sits
          below the AppBar (drawer + 1 = 1201) and above the main
          content, so it is always clickable but never overlaps the
          header. On mobile the temporary drawer already covers this
          case (hamburger in the AppBar). */}
      {hidden && !isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: 0,
            transform: 'translateY(-50%)',
            zIndex: 1100,
          }}
          data-testid="sidebar-show-handle"
        >
          <Tooltip title="Mostrar menú" placement="right" arrow>
            <IconButton
              aria-label="Mostrar menú"
              onClick={handleShowSidebar}
              size="small"
              sx={{
                borderRadius: '0 6px 6px 0',
                backgroundColor: 'background.paper',
                border: 1,
                borderLeft: 0,
                borderColor: 'divider',
                color: 'text.secondary',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </>
  );
}
